import React from 'react';
import { cn } from '../../../utils/cn';
import { CondoType } from '../../../utils/terminology';
import { formatRoleUnitDisplay, resolveRoleUnitParts } from '../../../utils/roleUnitDisplay';
import { roleUnitLink } from '../personModalRules';

/**
 * Which unit a liberação is for — the card's ONE mention of it.
 *
 * The card used to print it twice: a linked heading
 * ("Quadra Q16 – Rua Jefferson José de Moraes, 100 – Lote Q16LT8 - Rua Jefferson José de Moraes,
 * 100") and then this field with the same address underneath. The de-duplication could never
 * catch it, because the two are composed differently — one flat run-on line against an address
 * over its internal reference — so the strings never matched.
 *
 * The field won: it is labelled, it reads as an address, and it already splits the street from
 * the bloco/lote. So the LINK moved down here with it, carrying the behaviour the heading had —
 * `roleUnitLink` reads the id from all three places a payload may put it, and the host decides
 * how to open it (the Operational view needs its own handler; a bare /units navigation goes
 * nowhere there).
 *
 * See RoleUnitField.test.tsx.
 */

export interface RoleUnitFieldProps {
  role: any;
  condoType?: CondoType;
  darkMode?: boolean;
  /** Called with the unit id and the label shown, so the host can route or open a drawer. */
  onOpenUnit: (unitId: string, label: string) => void;
}

export const RoleUnitField: React.FC<RoleUnitFieldProps> = ({
  role,
  condoType,
  darkMode = false,
  onOpenUnit,
}) => {
  const meta = role?.role_metadata;
  const parts = resolveRoleUnitParts(meta, condoType);
  if (!parts) return null;

  const label = formatRoleUnitDisplay(meta, condoType) || parts.primary;
  const unitId = roleUnitLink(role);

  const address = (
    <>
      <span className={cn('block text-sm font-semibold', darkMode ? 'text-gray-100' : 'text-gray-900')}>
        {parts.primary}
      </span>
      {parts.secondary && (
        <span className={cn('block text-xs', darkMode ? 'text-gray-400' : 'text-gray-500')}>
          {parts.secondary}
        </span>
      )}
    </>
  );

  return (
    <div className="col-span-2">
      <p className={cn('text-[10px] uppercase tracking-widest mb-0.5', darkMode ? 'text-gray-500' : 'text-gray-400')}>
        Unidade destino
      </p>
      {/* Plain text without an id: a dead link is worse than no link. */}
      {unitId ? (
        <button
          type="button"
          onClick={() => onOpenUnit(unitId, label)}
          title={`Ver unidade ${label}`}
          className={cn(
            'text-left hover:underline underline-offset-2 transition-colors',
            darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800',
          )}
        >
          {address}
        </button>
      ) : (
        <div>{address}</div>
      )}
    </div>
  );
};

export default RoleUnitField;
